import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const categories = [
  { key: "hope", label: "Hope", color: "#C9A84C", bg: "rgba(201,168,76,0.1)", border: "rgba(201,168,76,0.3)", placeholder: "What do you dream Forge SA can become for South Africa?" },
  { key: "frustration", label: "Frustration", color: "#C0392B", bg: "rgba(192,57,43,0.07)", border: "rgba(192,57,43,0.2)", placeholder: "What has frustrated you? What do you wish was different?" },
  { key: "suggestions", label: "Suggestions", color: "#185FA5", bg: "rgba(91,155,213,0.1)", border: "rgba(91,155,213,0.3)", placeholder: "How do you think Forge SA could work better?" },
  { key: "expert", label: "Expert Advice", color: "#2E7D32", bg: "rgba(46,125,50,0.08)", border: "rgba(46,125,50,0.2)", placeholder: "Share your professional insight to help Forge SA build better." },
];

export default function TheForge() {
  const [activeTab, setActiveTab] = useState("hope");
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("forge_posts")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setPosts(data);
    setLoading(false);
  };

  const handlePost = async () => {
    if (!user) {
      window.location.href = "/register";
      return;
    }
    if (!content.trim()) return;

    setPosting(true);
    const firstName = user?.user_metadata?.first_name || "Anonymous";
    const province = user?.user_metadata?.province || "";

    await supabase.from("forge_posts").insert({
      user_id: user.id,
      name: firstName,
      province: province,
      category: activeTab,
      content: content.trim(),
    });

    setContent("");
    setPosting(false);
    fetchPosts();
  };

  const handleLike = async (id, currentLikes) => {
    if (!user) {
      window.location.href = "/register";
      return;
    }
    await supabase.from("forge_posts").update({ likes: currentLikes + 1 }).eq("id", id);
    fetchPosts();
  };

  const timeAgo = (dateStr) => {
    const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString("en-ZA");
  };

  const activeCat = categories.find((c) => c.key === activeTab);
  const filteredPosts = posts.filter((p) => p.category === activeTab);

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .forge-header { padding: 48px 32px 40px; }
        .forge-tabs { display: flex; gap: 6px; padding: 24px 32px 0; max-width: 760px; margin: 0 auto; overflow-x: auto; }
        .forge-tab { padding: 9px 16px; border-radius: 99px; font-size: 12px; font-weight: 700; white-space: nowrap; cursor: pointer; border: 1px solid; flex-shrink: 0; font-family: var(--font-body); }
        .forge-composer { max-width: 760px; margin: 20px auto 0; padding: 0 32px; }
        .forge-composer-box { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 18px 20px; }
        .forge-input { width: 100%; min-height: 60px; border: 1px solid #EDE8DF; border-radius: 10px; padding: 12px; font-size: 13px; color: #4A4A6A; font-family: var(--font-body); resize: vertical; outline: none; }
        .forge-feed { max-width: 760px; margin: 28px auto 0; padding: 0 32px 64px; }
        .forge-post { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 20px 22px; margin-bottom: 14px; }
        .forge-post-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .forge-avatar { width: 34px; height: 34px; border-radius: 50%; background: #1B3A6B; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; flex-shrink: 0; }
        @media (max-width: 768px) {
          .forge-header { padding: 32px 20px 28px; }
          .forge-tabs { padding: 20px 20px 0; }
          .forge-composer { padding: 0 20px; }
          .forge-feed { padding: 0 20px 48px; }
        }
      `}</style>

      <Navbar />

      {/* HEADER */}
      <div className="forge-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 18 }}>
          A Space To Be Heard
        </div>
        <h1 style={{ fontSize: "clamp(26px, 5vw, 36px)", fontWeight: 900, color: "#1A1A2E", letterSpacing: "-0.02em", marginBottom: 12 }}>
          The <span style={{ color: "#1B3A6B" }}>Forge.</span>
        </h1>
        <p style={{ fontSize: 13, color: "#888", lineHeight: 1.7, maxWidth: 480, margin: "0 auto" }}>
          This is where South Africans speak honestly. Share your hope, your frustration, your ideas, or your expertise. Every voice shapes what Forge SA becomes.
        </p>
      </div>

      {/* TABS */}
      <div className="forge-tabs">
        {categories.map((c) => (
          <div
            key={c.key}
            onClick={() => setActiveTab(c.key)}
            className="forge-tab"
            style={{
              background: activeTab === c.key ? c.color : c.bg,
              color: activeTab === c.key ? "#fff" : c.color,
              borderColor: activeTab === c.key ? c.color : c.border,
            }}
          >
            {c.label}
          </div>
        ))}
      </div>

      {/* COMPOSER */}
      <div className="forge-composer">
        <div className="forge-composer-box">
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
            Share your {activeCat.label.toLowerCase()}
          </div>
          <textarea
            className="forge-input"
            placeholder={activeCat.placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button
              onClick={handlePost}
              disabled={posting}
              style={{ background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 99, padding: "9px 22px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)", opacity: posting ? 0.6 : 1 }}
            >
              {posting ? "Posting..." : user ? "Post" : "Sign in to post"}
            </button>
          </div>
        </div>
      </div>

      {/* FEED */}
      <div className="forge-feed">
        {loading ? (
          <div style={{ textAlign: "center", color: "#888", fontSize: 13, padding: "40px 0" }}>Loading...</div>
        ) : filteredPosts.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", fontSize: 13, padding: "40px 0" }}>
            No posts yet under {activeCat.label}. Be the first to share.
          </div>
        ) : (
          filteredPosts.map((post) => (
            <div key={post.id} className="forge-post">
              <div className="forge-post-head">
                <div className="forge-avatar">{post.name?.charAt(0).toUpperCase() || "A"}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{post.name}</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {post.province ? `${post.province} · ` : ""}{timeAgo(post.created_at)}
                  </div>
                </div>
                <div style={{
                  fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                  padding: "3px 9px", borderRadius: 99, marginLeft: "auto",
                  background: activeCat.bg, color: activeCat.color,
                }}>
                  {activeCat.label}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#4A4A6A", lineHeight: 1.7, marginBottom: 12 }}>
                {post.content}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: "#888" }}>
                <div
                  onClick={() => handleLike(post.id, post.likes)}
                  style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                  {post.likes || 0}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
}